import { Fragment, useState } from 'react';
import {
  BREAKPOINTS_WIDTHS,
  formatNumber,
  useWindowSize,
} from '@belong/common';
import { IconCaretDown, IconInfo } from '@belong/icons';
import { Image, Link, Text, Tooltip } from '@belong/ui';
import clsx from 'clsx';
import { PayoutPlanVariationModel } from 'src/api/models';
import { usePayoutPlans, useRegions } from 'src/features/app';
import { From } from 'src/modals/region-select/region-select';
import { useStore } from 'src/store';
import { regionSelector } from 'src/store/region';
import css from './price-table.module.css';
import { STRINGS } from './price-table.strings';
import { TablePayoutPlans } from './table-payout-plans';

/**
 * The strings file declares every row's `options` as a plain array, so each read
 * is `string | undefined` — which is why the direct `options[0]`/`options[1]`
 * reads further down are asserted; every row literal there has exactly two. Every row has exactly two, and the second of the fee
 * rows below is `'<amount>/<period>'`, split over two lines on small screens.
 */
function feeRow(item: { name: string; options: string[] }) {
  const [belong = '', others = ''] = item.options;
  const [amount = '', period = ''] = others.split('/');

  return { name: item.name, belong, others, amount, period };
}

function TooltipRowName({
  name,
  text,
  isMobile,
  nameClassName = 'whitespace-pre-line',
  isOption,
}: {
  name: string;
  text: string;
  isMobile: boolean;
  nameClassName?: string;
  isOption?: boolean;
}) {
  return (
    <div
      className={clsx(
        'gap-xs flex justify-between md:justify-start',
        isOption && 'items-center',
      )}
    >
      <span className={clsx(nameClassName, isOption && 'font-regular')}>
        {name}
      </span>
      <Tooltip
        content={
          <div className="bg-dark-navy p-2sm max-w-xs rounded outline-none md:max-w-md">
            <Text fontWeight="semibold" className="text-white">
              {text}
            </Text>
          </div>
        }
        contentProps={
          isMobile
            ? {
                side: 'top',
                align: 'start',
                alignOffset: -100,
                className: 'outline-none',
              }
            : {}
        }
        arrowProps={{
          className: 'fill-dark-navy',
        }}
      >
        <div className="gap-2xs flex shrink-0 cursor-pointer flex-row justify-center">
          <IconInfo width={20} />
        </div>
      </Tooltip>
    </div>
  );
}

const getHomeownerPriceTableConfig = ({
  regionId,
  from,
  payoutPlan,
  isMobile,
}: {
  regionId?: number;
  from: From;
  payoutPlan?: Record<string, PayoutPlanVariationModel>;
  isMobile: boolean;
}) => {
  const hasVariableFees =
    payoutPlan?.Monthly?.defaultPayoutPlanType === 'Variable';
  const hasVariableNoMinimumFees =
    payoutPlan?.Monthly?.defaultPayoutPlanType === 'VariableNoMinimum';
  const isOnNationallyRegion = regionId === 0;
  const item15 = feeRow(STRINGS.item15);
  const item16 = feeRow(STRINGS.item16);
  const item17 = feeRow(STRINGS.item17);
  const item18 = feeRow(STRINGS.item18);

  return {
    header: [
      <div key="belong" className="w-[59px] md:w-[67px]">
        <Image src="/logos/belong_logo_web.svg" alt="Belong" />
      </div>,
      <>
        <Text variant="p1" fontWeight="semibold" className="md:hidden">
          {'Property Managers'}
        </Text>
        <Text variant="body" fontWeight="semibold" className="hidden md:block">
          {'Property Managers'}
        </Text>
      </>,
    ],
    items: [
      ...(hasVariableFees
        ? [
            {
              name: (
                <TooltipRowName
                  name={STRINGS.variableFees.item1.name}
                  text={STRINGS.variableFees.item1.text}
                  isMobile={isMobile}
                />
              ),
              options: STRINGS.variableFees.item1.options,
            },
            {
              name: (
                <TooltipRowName
                  name={STRINGS.variableFees.item2.name}
                  text={STRINGS.variableFees.item2.text}
                  isMobile={isMobile}
                />
              ),
              options: STRINGS.variableFees.item2.options,
            },
          ]
        : [
            {
              name: (
                <TooltipRowName
                  name={STRINGS.fixedFees.item1.name}
                  text={STRINGS.fixedFees.item1.text}
                  isMobile={isMobile}
                  nameClassName="w-[calc(100%_-_20px)] whitespace-pre-line md:w-[164px] lg:w-auto"
                />
              ),
              options: isOnNationallyRegion
                ? STRINGS.fixedFees.item1Nationally.options
                : [
                    <>
                      <div className="sm:w-button-small w-auto md:w-auto">
                        <div className="contents md:hidden">
                          <Text fontWeight="semibold" variant="p1">
                            {STRINGS.fixedFees.item1.options[0]!}
                          </Text>
                        </div>
                        <div className="lg:w-5xl hidden md:contents lg:block xl:w-auto">
                          <Text fontWeight="semibold">
                            {STRINGS.fixedFees.item1.options[0]!}
                          </Text>
                        </div>
                      </div>
                    </>,
                    STRINGS.fixedFees.item1.options[1]!,
                  ],
            },
          ]),
      {
        name: (
          <TooltipRowName
            name={STRINGS.item1.name}
            text={STRINGS.item1.text}
            isMobile={isMobile}
          />
        ),
        options: STRINGS.item1.options,
      },
      {
        name: (
          <TooltipRowName
            name={STRINGS.item2.name}
            text={STRINGS.item2.text}
            isMobile={isMobile}
            nameClassName="whitespace-pre-line md:w-[157px] lg:w-auto"
          />
        ),
        options: STRINGS.item2.options,
      },
      {
        name: (
          <TooltipRowName
            name={STRINGS.item3.name}
            text={STRINGS.item3.text}
            isMobile={isMobile}
          />
        ),
        options: STRINGS.item3.options,
      },
      {
        name: (
          <TooltipRowName
            name={STRINGS.item4.name}
            text={STRINGS.item4.text}
            isMobile={isMobile}
          />
        ),
        options: STRINGS.item4.options,
      },
      {
        name: (
          <TooltipRowName
            name={STRINGS.item5.name}
            text={STRINGS.item5.text}
            isMobile={isMobile}
          />
        ),
        options: STRINGS.item5.options,
      },
    ],
    feeItems: [
      ...(hasVariableFees
        ? [
            {
              name: STRINGS.variableFees.item3.name,
              options: [
                <TablePayoutPlans
                  keyName="managementFeePercentage"
                  regionId={regionId}
                  key={1}
                  from={from}
                  payoutPlan={payoutPlan}
                  showTooltip
                  tooltipText={`$${formatNumber(payoutPlan?.Monthly?.minimumManagementFeeAmount)} Minimum`}
                />,
                STRINGS.variableFees.item3.options[1]!,
              ],
            },
            {
              name: STRINGS.variableFees.item4.name,
              options: [
                <TablePayoutPlans
                  keyName="placementFeePercentage"
                  regionId={regionId}
                  key={1}
                  from={from}
                  payoutPlan={payoutPlan}
                  showTooltip
                  tooltipText={`$${formatNumber(payoutPlan?.Monthly?.minimumPlacementFeeAmount)} Minimum`}
                />,
                STRINGS.variableFees.item4.options[1]!,
              ],
            },
            {
              name: STRINGS.fixedFees.item4.name,
              options: [
                <TablePayoutPlans
                  keyName="renewalFeePercent"
                  regionId={regionId}
                  key={1}
                  from={from}
                  payoutPlan={payoutPlan}
                  showTooltip
                  tooltipText={`$${formatNumber(payoutPlan?.Monthly?.minimumRenewalFeeAmount)} Minimum`}
                />,
                STRINGS.fixedFees.item4.options[1]!,
              ],
            },
          ]
        : hasVariableNoMinimumFees
          ? [
              {
                name: STRINGS.variableFees.item3.name,
                options: [
                  <TablePayoutPlans
                    keyName="managementFeePercentage"
                    regionId={regionId}
                    key={1}
                    from={from}
                    payoutPlan={payoutPlan}
                  />,
                  STRINGS.variableFees.item3.options[1]!,
                ],
              },
              {
                name: STRINGS.variableFees.item4.name,
                options: [
                  <TablePayoutPlans
                    keyName="placementFeePercentage"
                    regionId={regionId}
                    key={1}
                    from={from}
                    payoutPlan={payoutPlan}
                  />,
                  STRINGS.variableFees.item4.options[1]!,
                ],
              },
              {
                name: STRINGS.fixedFees.item4.name,
                options: [
                  <TablePayoutPlans
                    keyName="renewalFeePercent"
                    regionId={regionId}
                    key={1}
                    from={from}
                    payoutPlan={payoutPlan}
                    showTooltip
                    tooltipText={`$${formatNumber(payoutPlan?.Monthly?.minimumRenewalFeeAmount)} Minimum`}
                  />,
                  STRINGS.fixedFees.item4.options[1]!,
                ],
              },
            ]
          : [
              {
                name: STRINGS.fixedFees.item2.name,
                options: [
                  <TablePayoutPlans
                    keyName="managementFeePercentage"
                    regionId={regionId}
                    key={1}
                    from={from}
                    payoutPlan={payoutPlan}
                  />,
                  STRINGS.fixedFees.item2.options[1]!,
                ],
              },
              {
                name: STRINGS.fixedFees.item3.name,
                options: [
                  <TablePayoutPlans
                    keyName="placementFeePercentage"
                    regionId={regionId}
                    key={1}
                    from={from}
                    payoutPlan={payoutPlan}
                  />,
                  STRINGS.fixedFees.item3.options[1]!,
                ],
              },
              {
                name: STRINGS.fixedFees.item4.name,
                options: [
                  <TablePayoutPlans
                    keyName="renewalFeePercent"
                    regionId={regionId}
                    key={1}
                    from={from}
                    payoutPlan={payoutPlan}
                  />,
                  STRINGS.fixedFees.item4.options[1]!,
                ],
              },
            ]),
      {
        name: (
          <TooltipRowName
            name={STRINGS.digitizationFee.name}
            text={STRINGS.digitizationFee.text}
            isMobile={isMobile}
          />
        ),
        options: STRINGS.digitizationFee.options,
      },
      {
        name: STRINGS.item6.name,
        options: STRINGS.item6.options,
      },
      {
        name: STRINGS.item7.name,
        options: STRINGS.item7.options,
      },
      {
        name: STRINGS.item8.name,
        options: STRINGS.item8.options,
      },
      {
        name: STRINGS.item9.name,
        options: STRINGS.item9.options,
      },
      {
        name: STRINGS.item10.name,
        options: STRINGS.item10.options,
      },
      {
        name: STRINGS.item11.name,
        options: STRINGS.item11.options,
      },
      {
        name: STRINGS.item12.name,
        options: STRINGS.item12.options,
      },
      {
        name: STRINGS.item13.name,
        options: STRINGS.item13.options,
      },
      {
        name: STRINGS.item14.name,
        options: STRINGS.item14.options,
      },
      {
        name: item15.name,
        options: [
          item15.belong,
          <>
            <div className="px-2xs min-[420px]:hidden">
              <Text fontWeight="semibold" variant="p1">
                {item15.amount}/
              </Text>
              <Text fontWeight="semibold" variant="p1">
                {item15.period}
              </Text>
            </div>
            <div className="hidden min-[420px]:block">
              <div className="contents md:hidden">
                <Text fontWeight="semibold" variant="p1">
                  {item15.others}
                </Text>
              </div>
              <div className="hidden md:contents">
                <Text fontWeight="semibold">{item15.others}</Text>
              </div>
            </div>
          </>,
        ],
      },
      {
        name: item16.name,
        options: [
          item16.belong,
          <>
            <div className="px-2xs min-[420px]:hidden">
              <Text fontWeight="semibold" variant="p1">
                {item16.amount}/
              </Text>
              <Text fontWeight="semibold" variant="p1">
                {item16.period}
              </Text>
            </div>
            <div className="hidden min-[420px]:block">
              <div className="contents md:hidden">
                <Text fontWeight="semibold" variant="p1">
                  {item16.others}
                </Text>
              </div>
              <div className="hidden md:contents">
                <Text fontWeight="semibold">{item16.others}</Text>
              </div>
            </div>
          </>,
        ],
      },
      {
        name: item17.name,
        options: [
          item17.belong,
          <>
            <div className="px-2xs min-[420px]:hidden">
              <Text fontWeight="semibold" variant="p1">
                {item17.amount}/
              </Text>
              <Text fontWeight="semibold" variant="p1">
                {item17.period}
              </Text>
            </div>
            <div className="hidden min-[420px]:block">
              <div className="contents md:hidden">
                <Text fontWeight="semibold" variant="p1">
                  {item17.others}
                </Text>
              </div>
              <div className="hidden md:contents">
                <Text fontWeight="semibold">{item17.others}</Text>
              </div>
            </div>
          </>,
        ],
      },
      {
        name: item18.name,
        options: [
          item18.belong,
          <>
            <div className="px-2xs min-[420px]:hidden">
              <Text fontWeight="semibold" variant="p1">
                {item18.amount}/
              </Text>
              <Text fontWeight="semibold" variant="p1">
                {item18.period}
              </Text>
            </div>
            <div className="hidden min-[420px]:block">
              <div className="contents md:hidden">
                <Text fontWeight="semibold" variant="p1">
                  {item18.others}
                </Text>
              </div>
              <div className="hidden md:contents">
                <Text fontWeight="semibold">{item18.others}</Text>
              </div>
            </div>
          </>,
        ],
      },
    ],
  };
};

type PriceTableItem = {
  name: React.ReactNode;
  options: (string | JSX.Element)[];
};

const getCellClassName = (stripe: number, hasBottomBorder = true): string =>
  clsx(
    'border-gray border-x-0 border-t-0 border-solid',
    stripe % 2 !== 0 && 'bg-light-gray',
    hasBottomBorder ? 'border-b' : 'border-b-0',
  );

const FIRST_COL_CLASS_NAME = 'px-xs col-span-3 flex flex-col justify-center';

const COLLAPSE_DURATION_CLASS_NAME =
  'duration-300 motion-reduce:transition-none';

function RowName({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Text as="div" className="md:hidden" variant="p1" fontWeight="semibold">
        {children}
      </Text>
      <Text
        as="div"
        className="hidden md:block"
        variant="body"
        fontWeight="semibold"
      >
        {children}
      </Text>
    </>
  );
}

function OptionText({ children }: { children: string }) {
  return (
    <>
      <Text fontWeight="semibold" variant="p1" className="md:hidden">
        {children}
      </Text>
      <Text fontWeight="semibold" variant="body" className="hidden md:block">
        {children}
      </Text>
    </>
  );
}

function OptionCell({
  stripe,
  isBelong,
  hasBottomBorder,
  isLast,
  rowSpan,
  className,
  children,
}: {
  stripe: number;
  isBelong: boolean;
  hasBottomBorder?: boolean;
  isLast?: boolean;
  rowSpan?: number;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={clsx(
        getCellClassName(stripe, hasBottomBorder),
        'col-span-2 flex items-center justify-center text-center',
        isBelong ? 'px-0' : 'px-2xs',
      )}
      style={rowSpan ? { gridRow: `span ${rowSpan}` } : undefined}
    >
      <div
        className={clsx(
          'px-2xs flex items-center justify-center',
          isBelong && 'border-x-navy border-x-4 border-y-0 border-solid',
          isBelong && css.belongItemCell,
          isLast && 'rounded-b-small border-b-navy border-b-4',
          isLast && css.belongTrailingCell,
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

function PriceRow({
  item,
  stripe,
  isLast,
}: {
  item: PriceTableItem;
  stripe: number;
  isLast?: boolean;
}) {
  return (
    <>
      <div
        className={clsx(
          getCellClassName(stripe),
          FIRST_COL_CLASS_NAME,
          'min-h-[46px] py-xs',
        )}
      >
        <RowName>{item.name}</RowName>
      </div>
      {item.options.map((option, optionIdx) => (
        <OptionCell
          key={optionIdx}
          stripe={stripe}
          isBelong={optionIdx === 0}
          isLast={isLast}
        >
          {typeof option === 'string' ? (
            <OptionText>{option}</OptionText>
          ) : (
            option
          )}
        </OptionCell>
      ))}
    </>
  );
}

function BundleDivider({
  isCollapsed,
  className,
}: {
  isCollapsed: boolean;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={clsx(
        'border-gray absolute bottom-0 border-x-0 border-b-0 border-t border-dashed transition-opacity',
        COLLAPSE_DURATION_CLASS_NAME,
        isCollapsed ? 'opacity-0' : 'opacity-100',
        className,
      )}
    />
  );
}

function RowCollapse({
  isCollapsed,
  children,
}: {
  isCollapsed: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      aria-hidden={isCollapsed || undefined}
      className={clsx(
        'grid transition-[grid-template-rows,opacity,visibility]',
        COLLAPSE_DURATION_CLASS_NAME,
        isCollapsed
          ? 'invisible grid-rows-[0fr] opacity-0'
          : 'grid-rows-[1fr] opacity-100',
      )}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

function IncomeLockRows({
  stripe,
  isMobile,
}: {
  stripe: number;
  isMobile: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const isCollapsed = !isOpen;
  const { options } = STRINGS.incomeLock;

  return (
    <>
      <div
        className={clsx(
          getCellClassName(stripe, false),
          FIRST_COL_CLASS_NAME,
          'pt-xs transition-[padding]',
          COLLAPSE_DURATION_CLASS_NAME,
          isCollapsed && 'pb-xs',
        )}
      >
        <RowName>
          <button
            type="button"
            aria-expanded={isOpen}
            onClick={() => setIsOpen((open) => !open)}
            className="gap-xs flex w-full cursor-pointer items-center border-0 bg-transparent p-0 text-left"
            style={{ color: 'inherit', font: 'inherit' }}
          >
            <span className="gap-2xs md:gap-xs flex flex-col items-start md:flex-row md:items-center">
              <Text
                as="span"
                variant="p1"
                fontWeight="semibold"
                className="rounded-small bg-navy px-xs text-white md:order-last"
              >
                {STRINGS.incomeLock.optional}
              </Text>
              <span className="whitespace-nowrap">
                {STRINGS.incomeLock.name}
              </span>
            </span>
            <IconCaretDown
              width={16}
              className={clsx(
                'ml-auto shrink-0 transition-transform',
                COLLAPSE_DURATION_CLASS_NAME,
                isOpen && 'rotate-180',
              )}
            />
          </button>
        </RowName>
      </div>
      <OptionCell stripe={stripe} isBelong hasBottomBorder={false}>
        {isCollapsed && (
          <OptionText>{STRINGS.incomeLock.collapsedPrice}</OptionText>
        )}
      </OptionCell>
      <OptionCell stripe={stripe} isBelong={false} rowSpan={options.length + 1}>
        <OptionText>{STRINGS.incomeLock.unavailable}</OptionText>
      </OptionCell>
      {options.map((option, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === options.length - 1;
        const isBeforeBundle = idx === options.length - 2;

        return (
          <Fragment key={option.name}>
            <div
              className={clsx(
                getCellClassName(stripe, isLast),
                FIRST_COL_CLASS_NAME,
                'relative transition-[padding]',
                COLLAPSE_DURATION_CLASS_NAME,
                isCollapsed && 'py-0',
                !isCollapsed && isLast && 'py-xs',
                !isCollapsed && !isLast && (isFirst ? 'pt-sm' : 'pt-2xs'),
                !isCollapsed &&
                  !isLast &&
                  (isBeforeBundle ? 'pb-xs' : 'pb-2xs'),
              )}
            >
              <RowCollapse isCollapsed={isCollapsed}>
                <RowName>
                  <TooltipRowName
                    name={option.name}
                    text={option.text}
                    isMobile={isMobile}
                    isOption
                  />
                  {isLast && (
                    <Link
                      href={STRINGS.incomeLock.learnMoreHref}
                      className="mt-sm text-green w-fit"
                    >
                      {STRINGS.incomeLock.learnMore}
                    </Link>
                  )}
                </RowName>
              </RowCollapse>
              {isBeforeBundle && (
                <BundleDivider
                  isCollapsed={isCollapsed}
                  className="inset-x-xs"
                />
              )}
            </div>
            <OptionCell
              stripe={stripe}
              isBelong
              hasBottomBorder={isLast}
              className={clsx(isBeforeBundle && 'relative')}
            >
              <RowCollapse isCollapsed={isCollapsed}>
                <OptionText>{option.price}</OptionText>
              </RowCollapse>
              {isBeforeBundle && (
                <BundleDivider
                  isCollapsed={isCollapsed}
                  className="inset-x-sm"
                />
              )}
            </OptionCell>
          </Fragment>
        );
      })}
    </>
  );
}

type PriceTableProps = {
  regionId?: number;
  from?: From;
};

export function PriceTable({
  regionId,
  from = 'homeowners',
}: PriceTableProps): JSX.Element {
  const { width } = useWindowSize();
  const isMobile = width < BREAKPOINTS_WIDTHS.LG;
  const { regions } = useRegions();
  const selectedRegion = useStore(regionSelector);
  const { payoutPlans } = usePayoutPlans();

  const propRegion = regions.find((region) => region.id === regionId);
  const { id } = propRegion || selectedRegion;
  const payoutPlan = payoutPlans?.[id || 0]?.DEFAULT;
  const config = getHomeownerPriceTableConfig({
    regionId,
    from,
    payoutPlan,
    isMobile,
  });
  const incomeLockStripe = config.items.length;

  return (
    <div className="relative w-full">
      <div className="my-2xs border-gray grid w-full auto-rows-min grid-cols-7 border border-b-0 border-solid">
        <div
          className={clsx(
            getCellClassName(-1),
            FIRST_COL_CLASS_NAME,
            'min-h-[46px] py-xs',
          )}
        />
        {config.header.map((header, idx) => (
          <div
            className={clsx(
              getCellClassName(-1),
              'col-span-2 flex items-center justify-center text-center',
              idx === 0 ? 'px-0' : 'px-2xs',
            )}
            key={idx}
          >
            <div
              className={clsx(
                idx === 0 &&
                  'rounded-small border-x-navy border-t-navy flex items-center justify-center border-x-4 border-b-0 border-t-4 border-solid',
                idx === 0 && css.belongTrailingCell,
              )}
            >
              {header}
            </div>
          </div>
        ))}
        {config.items.map((item, idx) => (
          <PriceRow key={idx} item={item} stripe={idx} />
        ))}
        <IncomeLockRows stripe={incomeLockStripe} isMobile={isMobile} />
        {config.feeItems.map((item, idx) => (
          <PriceRow
            key={idx}
            item={item}
            stripe={incomeLockStripe + 1 + idx}
            isLast={idx === config.feeItems.length - 1}
          />
        ))}
      </div>
    </div>
  );
}
